import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { ChartFragment, Section } from 'models/api-response';
import { ChartActionsComponentProps, ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { appHelper, chartHelper } from 'helpers';
import { chartService } from 'services';
import { appSelectors } from 'redux/ducks/app';
import { studentSelectors } from 'redux/ducks/student';
import { withChartLogic } from 'components/common';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import {
  DISABILITY_OTHER_VALUE,
  FIELD_TYPE_MAP,
  FORM_FIELD_LABEL_MAP,
  FormField,
  FormFieldLabel,
  PROVIDER_NURSE_FIELD,
  PROVIDER_NURSE_LABEL,
  REQUIRED_FORM_FIELDS,
  STATIC_ACK_FIELD,
  STATIC_LEGAL_TEXT,
  SectionTitle
} from './constants';
import { InformedConsentChartHistory } from './InformedConsentHistory';
import InformedConsentView from './InformedConsentView';

interface InformedConsentState {
  chartHistory: InformedConsentChartHistory[];
  fragments: ChartFragment[];
}

export interface InformedConsentProps extends ChartComponentProps {
  authorName?: string;
  userRole?: string;
}

const hasFieldValue = (field: ChartMetaFormField | undefined): boolean => field?.value !== undefined && field.value !== null && String(field.value).trim() !== '';

const areRequiredFieldsFilled = (fields: Map<string, ChartMetaFormField>): boolean =>
  Array.from(fields.values()).every((field) => !field.isRequired || hasFieldValue(field)) &&
  Array.from(REQUIRED_FORM_FIELDS).every((fieldName) => hasFieldValue(fields.get(fieldName)));

const buildProviderNurseRecord = (isAuthor: boolean, authorName: string, student: { firstName?: string; lastName?: string } | undefined) => {
  const resolvedName = isAuthor ? (authorName || '').trim() : `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim();
  if (!resolvedName) return null;
  return chartService.buildPlainRecord({
    formField: PROVIDER_NURSE_FIELD,
    title: PROVIDER_NURSE_LABEL,
    content: resolvedName,
    value: resolvedName,
    contentId: ''
  });
};

const isDisabilityOtherSelected = (formFieldMap: Map<string, ChartMetaFormField>): boolean =>
  Boolean(formFieldMap.get(FormField.DISABILITY_ACCOMMODATIONS)?.chartContent?.some((item) => item.selected && item.value === DISABILITY_OTHER_VALUE));

const buildAcknowledgementRecord = (fieldName: string, title: string, content: string, value = '') =>
  chartService.buildPlainRecord({ formField: fieldName, title, content, value });

const buildStaticAcknowledgementRecord = (fieldName: string, title: string) => buildAcknowledgementRecord(fieldName, title, '');

const displayLabel = (field: ChartMetaFormField | undefined): string => {
  const selectedOption = field?.chartContent?.find((item) => item.selected);
  return selectedOption?.label ?? String(field?.value ?? '');
};

const buildFieldAcknowledgementRecord = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField, title: string) => {
  const field = formFieldMap.get(fieldName);
  return buildAcknowledgementRecord(fieldName, title, hasFieldValue(field) ? displayLabel(field) : '', hasFieldValue(field) ? String(field.value) : '');
};

const valueLine = (label: string, field: ChartMetaFormField | undefined): string | null => (hasFieldValue(field) ? `${label}: ${displayLabel(field)}` : null);

const buildMaterialRisksRecord = (formFieldMap: Map<string, ChartMetaFormField>) => {
  const risks = [FormField.MATERIAL_RISK_A, FormField.MATERIAL_RISK_B, FormField.MATERIAL_RISK_C, FormField.MATERIAL_RISK_D]
    .map((fieldName) => formFieldMap.get(fieldName))
    .filter(hasFieldValue)
    .map((field) => displayLabel(field));
  return buildAcknowledgementRecord(FormField.MATERIAL_RISK_A, STATIC_LEGAL_TEXT.ITEM_4_INTRO, risks.join('\n'));
};

const buildInterpreterRecord = (formFieldMap: Map<string, ChartMetaFormField>) => {
  const lines = [
    valueLine('Name', formFieldMap.get(FormField.INTERPRETER_NAMES)),
    valueLine('Modality', formFieldMap.get(FormField.INTERPRETER_MODALITY)),
    valueLine('Language', formFieldMap.get(FormField.LANGUAGE))
  ].filter(Boolean);
  const title = `${STATIC_LEGAL_TEXT.INTERPRETER_SECTION_TITLE}\n${STATIC_LEGAL_TEXT.INTERPRETER_MATERIALS_LINE}`;
  return buildAcknowledgementRecord(FormField.INTERPRETER_NAMES, title, lines.join('\n'));
};

const buildDisabilityAndPatientRecord = (formFieldMap: Map<string, ChartMetaFormField>) => {
  const selectedAccommodations = formFieldMap.get(FormField.DISABILITY_ACCOMMODATIONS)?.chartContent?.filter((item) => item.selected) ?? [];
  const lines = [
    selectedAccommodations.length ? selectedAccommodations.map((item) => item.label).join(', ') : null,
    isDisabilityOtherSelected(formFieldMap) ? valueLine('Other', formFieldMap.get(FormField.DISABILITY_OTHER)) : null,
    valueLine('Name', formFieldMap.get(FormField.PATIENT_NAME)),
    valueLine('Signature', formFieldMap.get(FormField.PATIENT_SIGNATURE))
  ].filter(Boolean);
  return buildAcknowledgementRecord(FormField.DISABILITY_ACCOMMODATIONS, `${STATIC_LEGAL_TEXT.DISABILITY_SECTION_TITLE} & Patient Details`, lines.join('\n'));
};

const buildSignatureRecord = (signatureField: FormField, formFieldMap: Map<string, ChartMetaFormField>) => {
  const lines = [valueLine('Signature', formFieldMap.get(signatureField))].filter(Boolean);
  return buildAcknowledgementRecord(signatureField, FORM_FIELD_LABEL_MAP[signatureField], lines.join('\n'));
};

const buildGuardianRecord = (formFieldMap: Map<string, ChartMetaFormField>) => {
  const field = formFieldMap.get(FormField.GUARDIAN_NAME);
  return buildAcknowledgementRecord(FormField.GUARDIAN_NAME, `I, ${STATIC_LEGAL_TEXT.GUARDIAN_HELPER}`, hasFieldValue(field) ? String(field.value) : '');
};


class InformedConsent extends Component<InformedConsentProps, InformedConsentState> {
  static displayName = 'InformedConsent';

  constructor(props) {
    super(props);
    this.state = {
      chartHistory: [],
      fragments: []
    };
  }

  componentDidMount() {
    this.props.initState(this.buildFormFields());
    appHelper.useLoader(this.props.loadChartData().then(this.updateChartHistory));
  }

  buildFormFields = (): Map<string, ChartMetaFormField> => {
    const dataMap = new Map<string, ChartMetaFormField>();
    Object.values(FormField).forEach((fieldName) => {
      const field = chartHelper.createFormField({
        name: fieldName,
        type: FIELD_TYPE_MAP[fieldName],
        label: FORM_FIELD_LABEL_MAP[fieldName] || ''
      });
      field.isRequired = REQUIRED_FORM_FIELDS.has(fieldName as FormField);
      dataMap.set(fieldName, field);
    });
    return dataMap;
  };

  updateChartHistory = (apiRes) => {
    const chartHistory: InformedConsentChartHistory[] = (apiRes?.data || []).map((fragment: ChartFragment) => ({
      active: fragment.active,
      id: fragment.fragmentId,
      chartingAt: fragment.chartingAt,
      createdAt: fragment.createdAt,
      creator: fragment.creator,
      modifier: fragment.modifier,
      [FormField.PROCEDURE_DESCRIPTION]: chartHelper.getFragmentValue(fragment, SectionTitle.INFORMED_CONSENT, FormField.PROCEDURE_DESCRIPTION),
      [PROVIDER_NURSE_FIELD]: chartHelper.getFragmentValue(fragment, SectionTitle.INFORMED_CONSENT, PROVIDER_NURSE_FIELD),
      [FormField.PATIENT_NAME]: chartHelper.getFragmentValue(fragment, SectionTitle.INFORMED_CONSENT, FormField.PATIENT_NAME),
      [FormField.LIKELIHOOD_OF_SUCCESS]: chartHelper.getFragmentValue(fragment, SectionTitle.INFORMED_CONSENT, FormField.LIKELIHOOD_OF_SUCCESS)
    }));
    this.setState({ chartHistory, fragments: apiRes?.data || [] });
  };

  handleCancelClick = () => this.props.handleDiscardClick(undefined, this.buildFormFields());

  handleSaveClick = () => {
    this.props.saveChart([this.buildFragment()], { defaultFormFields: this.buildFormFields(), afterSave: this.afterSave });
  };

  afterSave = () => {
    this.props.loadChartData().then(this.updateChartHistory);
    if (!this.props.isAuthor) {
      this.props.navigateToSavedPatientCharting();
    }
  };

  buildFragment = () => {
    const record = {
      chartTitle: SectionTitle.INFORMED_CONSENT,
      fragmentTitle: SectionTitle.INFORMED_CONSENT,
      records: this.createSections()
    };
    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const basicInfo = chartService.createBaseFragment({ chartingTime: this.props.chartingTime });
    return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
  };

  createSections = (): Section[] => {
    const { formFieldMap, isAuthor, authorName, student } = this.props;

    const records = [
      buildFieldAcknowledgementRecord(formFieldMap, FormField.PROCEDURE_DESCRIPTION, FormFieldLabel.PROCEDURE_DESCRIPTION),
      buildFieldAcknowledgementRecord(formFieldMap, FormField.CONDITION_DIAGNOSIS, FormFieldLabel.CONDITION_DIAGNOSIS),
      buildFieldAcknowledgementRecord(formFieldMap, FormField.PURPOSE, FormFieldLabel.PURPOSE),
      buildMaterialRisksRecord(formFieldMap),
      buildStaticAcknowledgementRecord(STATIC_ACK_FIELD.ITEM_5, STATIC_LEGAL_TEXT.ITEM_5),
      buildFieldAcknowledgementRecord(formFieldMap, FormField.LIKELIHOOD_OF_SUCCESS, FormFieldLabel.LIKELIHOOD_OF_SUCCESS),
      buildFieldAcknowledgementRecord(formFieldMap, FormField.PROGNOSIS_IF_DECLINED, FormFieldLabel.PROGNOSIS_IF_DECLINED),
      buildFieldAcknowledgementRecord(formFieldMap, FormField.PRACTICAL_ALTERNATIVES, FormFieldLabel.PRACTICAL_ALTERNATIVES),
      buildStaticAcknowledgementRecord(STATIC_ACK_FIELD.ITEM_9, STATIC_LEGAL_TEXT.ITEM_9),
      buildStaticAcknowledgementRecord(STATIC_ACK_FIELD.ITEM_10, STATIC_LEGAL_TEXT.ITEM_10),
      buildStaticAcknowledgementRecord(STATIC_ACK_FIELD.ITEM_11, STATIC_LEGAL_TEXT.ITEM_11),
      buildStaticAcknowledgementRecord(STATIC_ACK_FIELD.ITEM_12, STATIC_LEGAL_TEXT.ITEM_12),
      buildStaticAcknowledgementRecord(
        STATIC_ACK_FIELD.ITEM_13,
        `${STATIC_LEGAL_TEXT.ITEM_13_INTRO} a. ${STATIC_LEGAL_TEXT.ITEM_13_BULLET_1} b. ${STATIC_LEGAL_TEXT.ITEM_13_BULLET_2}`
      ),
      buildStaticAcknowledgementRecord(STATIC_ACK_FIELD.ITEM_14, STATIC_LEGAL_TEXT.ITEM_14),
      buildStaticAcknowledgementRecord(STATIC_ACK_FIELD.ITEM_15, STATIC_LEGAL_TEXT.ITEM_15),
      buildStaticAcknowledgementRecord(STATIC_ACK_FIELD.ITEM_16, STATIC_LEGAL_TEXT.ITEM_16),
      buildInterpreterRecord(formFieldMap),
      buildDisabilityAndPatientRecord(formFieldMap),
      buildSignatureRecord(FormField.WITNESS_SIGNATURE, formFieldMap),
      buildSignatureRecord(FormField.PHYSICIAN_SIGNATURE, formFieldMap),
      buildGuardianRecord(formFieldMap),
      buildFieldAcknowledgementRecord(formFieldMap, FormField.ADDITIONAL_COMMENTS, FormFieldLabel.ADDITIONAL_COMMENTS)
    ];

    const providerNurseRecord = buildProviderNurseRecord(isAuthor, authorName, student);
    if (providerNurseRecord) {
      records.unshift({ ...providerNurseRecord, isHidden: true });
    }

    return [{ sectionTitle: SectionTitle.INFORMED_CONSENT, records }];
  };

  deleteHistory = (record) => {
    const updateFragment = this.state.fragments.find((fragment) => fragment.fragmentId === record.id);
    if (!updateFragment) return;
    appHelper.useLoader(
      this.props
        .saveChartData({ ...updateFragment, active: false })
        .then(() => this.props.loadChartData())
        .then(this.updateChartHistory)
        .then(this.props.showDeleteSuccess)
    );
  };

  render() {
    const chartActionsProps: ChartActionsComponentProps = {
      enableDisplayRecordsButton: this.props.enableDisplayRecordsButton,
      enableSaveButton: this.props.hasUnsavedChanges && areRequiredFieldsFilled(this.props.formFieldMap),
      onCancelClick: this.handleCancelClick,
      onDisplayRecordsClick: this.props.displayAuthoringData,
      onSaveClick: this.handleSaveClick
    };
    const viewProps = {
      chartActionsProps,
      chartHistory: this.state.chartHistory,
      deleteHistory: this.deleteHistory,
      formFieldMap: this.props.formFieldMap,
      formSubmittedCount: this.props.formSubmittedCount,
      isAuthor: this.props.isAuthor,
      userRole: this.props.userRole
    };
    return <InformedConsentView {...viewProps} />;
  }
}

export { InformedConsent as BaseInformedConsent };

const mapStateToProps = (state) => ({
  authorName: studentSelectors.getAuthorName(state),
  userRole: appSelectors.getUserRole(state)
});

export default compose(connect(mapStateToProps), withSavedPatientChartsPage, withChartLogic)(InformedConsent);
